import { useMemo } from 'react'
import { effectiveActivityMs, latestActionLabel, effectiveActivityIso } from '../../utils/activityDate.js'

// UX67 commit 2: horizontal strip above the dual hero that surfaces
// the N most-recently-moved bills. Different signal from the heroes —
// this is "Congress just did these things," while the heroes are
// "Verdant's picks + the daily pulse."
//
// Click → scroll the main feed to that card (same mechanism UX54 uses
// for History-to-feed navigation). Falls back to opening /poll/<id>
// in a new tab if the card isn't in visibleItems (e.g., filtered out
// by jurisdiction / topic).

const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000  // 7 days
const MAX_ITEMS = 8

function daysAgoLabel(ms) {
  if (!ms) return ''
  const diff = Date.now() - ms
  const DAY = 24 * 60 * 60 * 1000
  if (diff < DAY) return 'Today'
  if (diff < 2 * DAY) return 'Yesterday'
  const n = Math.floor(diff / DAY)
  if (n < 7) return `${n} days ago`
  return effectiveActivityIso({ postedAt: new Date(ms) }) // short ISO
}

export function MovingStrip({ items, onOpen }) {
  const movers = useMemo(() => {
    const now = Date.now()
    return (items || [])
      .filter((it) => it.botName !== 'pulse')
      .filter((it) => it.kind === 'bill')
      .map((it) => ({ item: it, ms: effectiveActivityMs(it) }))
      .filter(({ ms }) => ms > 0 && (now - ms) < RECENT_WINDOW_MS)
      .sort((a, b) => b.ms - a.ms)
      .slice(0, MAX_ITEMS)
  }, [items])

  if (movers.length === 0) return null

  return (
    <section className="moving-strip" aria-label="What's moving in Congress">
      <div className="moving-strip-head">
        <span className="moving-strip-title">What&rsquo;s moving</span>
        <span className="moving-strip-sub">Bills with action in the last 7 days</span>
      </div>
      <div className="moving-strip-rail">
        {movers.map(({ item, ms }) => {
          const action = latestActionLabel(item)
          return (
            <button
              key={item.id}
              type="button"
              className="moving-card"
              onClick={() => onOpen?.(item.id)}
              aria-label={`Open ${item.title || item.billId || 'poll'}`}
            >
              <span className="moving-card-head">
                <span className={`moving-card-level ${item.level || 'federal'}`}>
                  {item.levelLabel || 'Federal'}
                </span>
                {item.billId && <span className="moving-card-billid">{item.billId}</span>}
              </span>
              <span className="moving-card-title">
                {item.title || item.plainSummary || 'Untitled'}
              </span>
              {action && (
                <span className="moving-card-action">
                  {action}
                </span>
              )}
              <span className="moving-card-when">{daysAgoLabel(ms)}</span>
            </button>
          )
        })}
      </div>
    </section>
  )
}
